import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { MAKHANA_SUPPLY, MAKHANA_GRADES, EXP, IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { Link } from "react-router-dom";
import { Check, ArrowRight, FileDown, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

export default function MakhanaHome() {
  return (
    <section id="makhana" className="relative py-20 lg:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-950/30 px-4 py-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Current Export Focus</span>
            </div>
            <h2 className="font-display mt-4 text-2xl sm:text-3xl lg:text-[2.6rem] font-bold text-white leading-tight">
              Premium Indian <span className="text-gradient-gold">Makhana</span> for Global Markets
            </h2>
            <p className="mt-4 text-base text-slate-300 leading-relaxed">
              AquaFionix is developing export-oriented Makhana (Fox Nuts) supply for international B2B buyers —
              importers, distributors, wholesalers, food companies and private-label brands.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {MAKHANA_SUPPLY.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
                  <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              {MAKHANA_GRADES.map((g) => (
                <span key={g} className="rounded-full border border-amber-400/30 bg-amber-950/30 px-3 py-1.5 text-xs font-medium text-amber-100">{g}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <button onClick={() => scrollToId("inquiry")} data-testid="request-makhana-quote" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 text-sm font-semibold text-[#1a0f00] shadow-[0_10px_30px_rgba(245,158,11,0.3)] hover:-translate-y-0.5 transition-all">
                Request Makhana Quote <ArrowRight className="h-4 w-4" />
              </button>
              <a href="/AquaFionix_Makhana_Fox_Nuts_Export_Catalogue.pdf" target="_blank" rel="noreferrer" download data-testid="download-product-info" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-all">
                <FileDown className="h-4 w-4 text-cyan-300" /> Download Makhana Catalogue
              </a>
            </div>
            <Link to="/makhana-export" data-testid="view-makhana-export" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors">
              View full Makhana export details <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-3">
              <motion.img whileHover={{ scale: 1.03 }} src={EXP.makhanaBowl} alt="Premium Indian makhana fox nuts" className="h-56 w-full object-cover rounded-2xl border border-white/10 col-span-2" />
              <img src={EXP.makhanaJars} alt="Makhana grades — 4, 5 and 6 Suta jars" className="h-40 w-full object-cover rounded-2xl border border-white/10" />
              <img src={EXP.makhanaProcessing} alt="Makhana grading and drying" className="h-40 w-full object-cover rounded-2xl border border-white/10" />
            </div>
          </Reveal>
        </div>

        {/* Makhana Pond highlight */}
        <Reveal delay={0.1}>
          <div className="mt-14 relative overflow-hidden rounded-[2rem] border border-amber-400/20" data-testid="makhana-pond-highlight">
            <img src={IMAGES.makhanaPond} alt="Makhana pond with lotus plants and seed pods" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#030D18]/90 via-[#030D18]/55 to-[#030D18]/20" />
            <div className="relative p-8 sm:p-12 max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-950/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">From Pond to Port</span>
              <h3 className="font-display mt-4 text-2xl sm:text-3xl font-bold text-white">It begins in the <span className="text-gradient-gold">Makhana Pond</span></h3>
              <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                Makhana (fox nuts) is harvested from pond cultivation — and it is our priority export.
                From the pond to grading, pouch packaging and shipment, we connect Indian makhana to global B2B buyers.
              </p>
              <Link to="/makhana-export" data-testid="makhana-pond-export-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors">
                Explore Makhana export <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
